import { z } from 'zod';

import { UPLOAD_PRESETS } from '@/lib/upload';

import { WARRANTY_DESCRIPTION_MAX } from './constants';

/**
 * **Claim status** — where one Warranty claim stands (`CONTEXT.md § Warranty & claims`). Not
 * **Warranty status**, which is a Device's coverage and lives on the devices feature.
 *
 * A closed enum on purpose: the backend's `WarrantyClaimStatus` has exactly these three members and
 * Approved/Rejected are terminal, so a fourth token would be a product change worth failing loudly
 * on rather than degrading to a neutral chip.
 */
export const WARRANTY_CLAIM_STATUSES = ['pending', 'approved', 'rejected'] as const;

export const warrantyClaimStatusSchema = z.enum(WARRANTY_CLAIM_STATUSES);

/**
 * One row of either claims listing.
 *
 * Display strings are `nullish` and normalised by {@link toWarrantyClaimSummary}, the same shape
 * `features/devices/schemas.ts` uses for the same fields on the device listing.
 */
const warrantyClaimSummaryWireSchema = z.object({
  id: z.string(),
  /** Grower-assigned nickname for the Device. */
  customName: z.string().nullish(),
  /** Manufacturer/default name, already unit-numbered by the server. */
  deviceName: z.string().nullish(),
  category: z.string().nullish(),
  status: warrantyClaimStatusSchema,
  claimedAt: z.string(),
});

/**
 * The listing's `meta`, as the backend serves it (`WarrantyClaimTrackingListMeta`). Cursor-paged:
 * `nextCursor` is opaque, sent back verbatim as `cursor`, and `null` once the list is exhausted.
 * `total` and the device-scoped `device` echo are on the wire and deliberately undeclared.
 */
const claimsPageMetaSchema = z.object({
  nextCursor: z.string().nullable(),
  /**
   * The whole eligibility surface (ADR-0019) — per-Device or account-wide, depending on the
   * endpoint. The server's verdict, never recomputed: it routes the panel between the intake
   * form and the listing, and decides whether the "Raise Warranty Claim" CTA is pinned.
   */
  canRaiseClaim: z.boolean(),
});

/**
 * `GET /devices/tracking/{customerDeviceId}/warranty-claims` **and**
 * `GET /warranty/tracking/claims` — deliberately one schema. The two endpoints differ only in
 * scope, never in shape, and `canRaiseClaim` is read per-Device on the first and account-wide on
 * the second (ADR-0019).
 */
export const warrantyClaimsResponseSchema = z.object({
  data: z.object({ claims: z.array(warrantyClaimSummaryWireSchema) }),
  meta: claimsPageMetaSchema,
});

export type WarrantyClaimStatus = z.infer<typeof warrantyClaimStatusSchema>;
export type WarrantyClaimSummaryWire = z.infer<typeof warrantyClaimSummaryWireSchema>;

/** One claim row, composed for display. */
export type WarrantyClaimSummary = {
  id: string;
  /** Row title: the Grower's custom name, falling back to the manufacturer name. */
  title: string;
  /** Sub-line "Device name | Category", with either half dropped when absent. */
  subtitle: string;
  status: WarrantyClaimStatus;
  /** UTC instant; rendered through `lib/date` per ADR-0007. */
  requestedAt: string;
};

/** The identity half both wire rows share; the list and the preview must not read differently. */
type ClaimIdentityWire = Pick<WarrantyClaimSummaryWire, 'customName' | 'deviceName' | 'category'>;

function toClaimIdentity(
  wire: ClaimIdentityWire,
): Pick<WarrantyClaimSummary, 'title' | 'subtitle'> {
  return {
    title: wire.customName ?? wire.deviceName ?? '',
    subtitle: [wire.deviceName, wire.category].filter(Boolean).join(' | '),
  };
}

export function toWarrantyClaimSummary(wire: WarrantyClaimSummaryWire): WarrantyClaimSummary {
  return {
    id: wire.id,
    ...toClaimIdentity(wire),
    status: wire.status,
    requestedAt: wire.claimedAt,
  };
}

/** One page of a Device's claims, normalised. */
export type WarrantyClaimsPage = {
  hasEligibleDevices: boolean;
  claims: WarrantyClaimSummary[];
  /** Opaque; `null` when there is no further page. */
  nextCursor: string | null;
};

/**
 * The five predefined reasons, the backend's `WarrantyClaimReason` verbatim.
 *
 * A closed enum, like {@link warrantyClaimStatusSchema} and unlike the timeline's `status`: the
 * card heading renders a label from this set, so an unknown token would have no copy to show.
 */
export const WARRANTY_CLAIM_REASONS = [
  'mechanical_or_electrical_failure',
  'manufacturing_defects',
  'performance_failure',
  'structural_flaws',
  'others',
] as const;

export const warrantyClaimReasonSchema = z.enum(WARRANTY_CLAIM_REASONS);

/** One evidence image uploaded with the claim. The array already arrives ordered. `url` is
 *  `null` when the server could not sign the object; such an image is dropped by
 *  {@link toWarrantyClaimDetail} rather than rendered broken. */
const warrantyClaimImageWireSchema = z.object({
  id: z.string(),
  url: z.string().nullable(),
});

/**
 * One Activity Timeline entry, as the **wire** names it.
 *
 * `status` is an un-narrowed `z.string()` on purpose — the exact opposite of the claim's own
 * `status` above. `created` lives only here, the backend types the field `string`, and a token
 * they add later must reach the panel (which degrades it to a neutral chip) rather than fail the
 * parse (ADR-0019).
 */
const warrantyTimelineEntryWireSchema = z.object({
  status: z.string(),
  at: z.string(),
});

/**
 * `GET /warranty/tracking/claims/{claimId}` — the Ticket Preview's whole payload.
 *
 * **`timeline` is required**, not `nullish` (grilling Q10/Q11, ADR-0019). We specified this
 * contract before the backend built it, so a missing array is a loud `contract_drift` in QA rather
 * than a Ticket Preview silently rendering without an AC-mandated section. The admin twin's
 * tolerance is exactly what hid its dead prefer-served branch for months.
 *
 * The server's pre-formatted `statusLabel`, `reasonLabel` and timeline `label`/`displayDateTime`
 * are on the wire and deliberately not parsed — copy lives in `constants.ts` and dates render
 * through `lib/date` (ADR-0007). zod strips unknown keys, so they cost nothing.
 */
export const warrantyClaimDetailWireSchema = z.object({
  id: z.string(),
  customName: z.string().nullish(),
  deviceName: z.string().nullish(),
  category: z.string().nullish(),
  reason: warrantyClaimReasonSchema,
  description: z.string(),
  status: warrantyClaimStatusSchema,
  /** Non-null only when the claim was rejected. */
  rejection: z.object({ reason: z.string() }).nullish(),
  images: z.array(warrantyClaimImageWireSchema),
  timeline: z.array(warrantyTimelineEntryWireSchema),
});

export type WarrantyClaimReason = z.infer<typeof warrantyClaimReasonSchema>;
/** One evidence image, composed for display. */
export type WarrantyClaimImage = { id: string; url: string };
/** One Activity Timeline entry, composed for display. */
export type WarrantyTimelineEntry = { status: string; timestamp: string };
export type WarrantyClaimDetailWire = z.infer<typeof warrantyClaimDetailWireSchema>;

/** One claim's full detail, composed for display. */
export type WarrantyClaimDetail = WarrantyClaimSummary & {
  reason: WarrantyClaimReason;
  description: string;
  /** Free text, Rejected claims only; `null` when the backend sent none. */
  rejectionReason: string | null;
  images: WarrantyClaimImage[];
  /** Served, ordered oldest first by the server. Never derived, never re-sorted. */
  timeline: WarrantyTimelineEntry[];
};

/**
 * Wire → display.
 *
 * The identity half is `toWarrantyClaimSummary`'s, not a second copy of it: the detail wire
 * carries every field the summary wire does, and one claim must not read differently on the list
 * and on its own preview.
 */
export function toWarrantyClaimDetail(wire: WarrantyClaimDetailWire): WarrantyClaimDetail {
  const timeline = wire.timeline.map((entry) => ({ status: entry.status, timestamp: entry.at }));
  return {
    id: wire.id,
    ...toClaimIdentity(wire),
    status: wire.status,
    // The detail wire carries no claim date of its own; the first timeline entry (Created) is it.
    requestedAt: timeline[0]?.timestamp ?? '',
    reason: wire.reason,
    description: wire.description,
    rejectionReason: wire.rejection?.reason ?? null,
    images: wire.images.flatMap((image) => (image.url ? [{ id: image.id, url: image.url }] : [])),
    timeline,
  };
}

/** One committed upload, as `<Upload>` hands it back (`UploadedFile` in `lib/upload`). */
const uploadedImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  name: z.string(),
  size: z.number(),
  contentType: z.string(),
});

/**
 * The intake form's values.
 *
 * The image count is enforced here regardless of what the webstore DTO tolerates
 * (ADR-0019, inherited from admin). `description` is required for **every** reason including
 * Others (113319), and trimmed so a field of spaces is not a description. `.max(2000)` never fires
 * in the UI — the textarea's own `maxLength` stops typing at the limit with no error, as the AC
 * requires — but it is the trust boundary for anything that reaches the mutation another way.
 */
export const warrantyClaimFormSchema = z.object({
  customerDeviceId: z.string().min(1),
  reason: warrantyClaimReasonSchema,
  description: z.string().trim().min(1).max(WARRANTY_DESCRIPTION_MAX),
  // The cap is the image preset's own `maxCount`, not a second copy of the number: `<Upload>`'s
  // `beforeUpload` refuses the sixth file against that same value, and two hand-written 5s could
  // drift apart silently.
  images: z.array(uploadedImageSchema).min(1).max(UPLOAD_PRESETS.componentImages.maxCount),
});

export type WarrantyClaimFormValues = z.infer<typeof warrantyClaimFormSchema>;

/** `POST /warranty/tracking/claims` — the wire body, built from the form values. */
export type CreateWarrantyClaimPayload = {
  customerDeviceId: string;
  reason: WarrantyClaimReason;
  description: string;
  /** Backend's CreateTrackingWarrantyClaimDto names this `imageAssetIds`, not `assetIds`. */
  imageAssetIds: string[];
};

/**
 * `POST /storage/tracking/pre-signed-url` (grilling Q7 — webstore-shaped, `customerDeviceId` in
 * place of `orderId`). `requiredHeaders` is provider-specific and passed straight through to the
 * PUT by `createPresignedUploader`.
 */
export const presignedUploadSchema = z.object({
  assetId: z.string(),
  uploadUrl: z.string(),
  expiresAt: z.string().nullish(),
  requiredHeaders: z.record(z.string(), z.string()).nullish(),
});

/**
 * One row of `GET /warranty/tracking/eligible-devices`.
 *
 * The wire also carries `category`; it is **deliberately undeclared**. zod drops unknown keys, no
 * frame renders a category in the dropdown, and declaring a field nothing reads is inert weight.
 *
 * There is no client-side filtering anywhere downstream: this array **is** the server's eligibility
 * verdict, and a Device that cannot be claimed against simply is not in it (ADR-0019).
 */
const eligibleDeviceSchema = z.object({
  customerDeviceId: z.string(),
  /** Grower-assigned nickname. Same nullish treatment as a claim row's names. */
  customName: z.string().nullish(),
  /** Manufacturer/default name. */
  deviceName: z.string().nullish(),
});

export const eligibleDevicesResponseSchema = z.array(eligibleDeviceSchema);

type EligibleDevice = z.infer<typeof eligibleDeviceSchema>;

/** One `<Select>` option: what the intake form's Device field is fed. */
export type DeviceOption = { value: string; label: string };

/**
 * One eligible Device as a `<Select>` option.
 *
 * 113319 requires each option to show "the Custom name(renamed) and Default name of the device".
 * **No Figma frame draws the open dropdown**, so the label is a single line joined with the ` | `
 * separator `toWarrantyClaimSummary` already uses for a row's sub-line — reusing the app's existing
 * idiom rather than inventing a two-line option no design specifies.
 *
 * `value` is the bare `customerDeviceId`: the only unit key this app knows (ADR-0019).
 */
export function toDeviceOption(device: EligibleDevice): DeviceOption {
  return {
    value: device.customerDeviceId,
    label: [device.customName, device.deviceName].filter(Boolean).join(' | '),
  };
}
